import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader, AlertTriangle, ArrowLeft } from 'lucide-react'
import { apiRequest } from '@/config/api'
import { apiClient, handleApiError } from '@/lib/api'

interface Position {
  id: string
  title: string
  status: string
  description?: string
  department?: string
  primary_location?: string
  employment_type?: string
  user_email?: string
  created_by_email?: string
  hiring_manager_email?: string
}

export default function AdminPositionReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [position, setPosition] = useState<Position | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      localStorage.setItem('token', token)
      localStorage.setItem('auth_token', token)
    }

    const loadPosition = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await apiClient.getPosition(id || '')
        if (response.success && response.data) {
          const data = (response.data as any).position || response.data
          setPosition(data)
        } else {
          setError('Position not found')
        }
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setLoading(false)
      }
    }

    if (id) loadPosition()
  }, [id, searchParams])

  const creatorEmail = position?.user_email || position?.created_by_email || position?.hiring_manager_email

  const handleDecision = async (decision: 'approved' | 'rejected') => {
    if (!position) return
    if (decision === 'rejected' && !suggestion.trim()) {
      alert('Please provide a suggestion/reason for rejection.')
      return
    }

    try {
      setActionLoading(true)
      const status = decision === 'approved' ? 'open' : 'rejected'
      await apiClient.updatePosition(position.id, { status, suggestion: suggestion.trim() })
      alert(`Position ${decision}`)
      navigate('/recruiter/positions')
    } catch (err) {
      alert('Action failed: ' + handleApiError(err))
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (error || !position) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <AlertTriangle className="w-12 h-12 text-red-500" />
        <p className="text-gray-700">{error || 'Position not found'}</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded">Go Back</button>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="bg-white shadow rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{position.title}</h1>
            <p className="text-sm text-gray-500">Status: {position.status}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleDecision('rejected')}
              disabled={actionLoading}
              className="inline-flex items-center px-4 py-2 border border-red-500 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </button>
            <button
              onClick={() => handleDecision('approved')}
              disabled={actionLoading}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Approve & Open
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm text-gray-700">
          <p><strong>Department:</strong> {position.department || 'N/A'}</p>
          <p><strong>Location:</strong> {position.primary_location || 'N/A'}</p>
          <p><strong>Type:</strong> {position.employment_type || 'N/A'}</p>
          <p><strong>Creator:</strong> {creatorEmail || 'Unknown'}</p>
        </div>

        <div className="space-y-2">
          <p className="text-sm text-gray-700"><strong>Suggestion / Feedback</strong></p>
          <textarea
            className="w-full border rounded-md p-3 text-sm"
            rows={4}
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Provide approval notes or rejection feedback"
          />
        </div>

        {position.description && (
          <div className="space-y-2">
            <p className="text-sm text-gray-700 font-semibold">Description</p>
            <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-md">
              {position.description}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
